import { Alert, Box, Button, OutlinedInput, Typography } from "@mui/material";
import {
  type ChangeEventHandler,
  type FormEventHandler,
  useState,
} from "react";

import { AppDialogActions, AppDialogContent } from "@dfp/react-components";
import { useAsyncCallback } from "@dfp/react-utils";

import {
  isAuthRequestedError,
  isPlatformRequestError,
} from "../errors/index.ts";
import type { RequestAuthorization } from "../types/requestAuthorization.ts";
import { useTT } from "../useTT.ts";
import { UserFriendlyId } from "./UserFriendlyId.tsx";
import { useTranslateAuthError } from "./useTranslateAuthError.ts";

export const REMOVE_MOBILE_KEY_AUTHORIZATION_DIALOG_NAME =
  "IP-removeRegisteredDevice";

type Props = RequestAuthorization;

export function RemoveMobileKeyAuthorizationDialog({
  errorAttributes: { authPersonName, emailShaded, requestId, userFriendlyId },
  onAuthError,
  onAuthorize,
  inputData,
}: Props) {
  const { tt } = useTT();
  const translateAuthError = useTranslateAuthError();
  const [authCode, setAuthCode] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const inputDataObj = inputData as object;
  const splitReason =
    ("reason" in inputDataObj &&
      typeof inputDataObj.reason === "string" &&
      inputDataObj.reason.split("|")) ||
    [];
  const hasMoreActiveRegistrations =
    splitReason.length > 1 ? splitReason[1] === "true" : false;

  const onFieldChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    setAuthCode(event.target.value);
    setErrorMessage(null);
  };

  const { callback: handleAuthorizeSubmit, isLoading: isSubmitting } =
    useAsyncCallback(async () => {
      if (authCode === "") {
        setErrorMessage(tt("dialog.Errors.fieldEmpty"));
        return;
      }

      setErrorMessage(null);

      try {
        await onAuthorize({
          authCode: authCode,
          authRequestId: requestId,
        });
      } catch (err) {
        if (isPlatformRequestError(err, 401, "RequestAuthorizationFailed")) {
          setErrorMessage(translateAuthError(err));
        } else {
          onAuthError(err);
        }
      } finally {
        setAuthCode("");
      }
    });

  const onSubmit: FormEventHandler = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    await handleAuthorizeSubmit();
  };

  const { callback: onClickResend, isLoading: isResendingCode } =
    useAsyncCallback(async () => {
      setErrorMessage(null);
      try {
        await onAuthorize();
      } catch (err) {
        if (!isAuthRequestedError(err)) {
          setErrorMessage(tt("dialog.Errors.authMethodNotConfigured"));
        }
      }
    });

  return (
    <>
      <form onSubmit={onSubmit}>
        <AppDialogContent>
          <Box gap={2} display="flex" flexDirection="column">
            {hasMoreActiveRegistrations ? null : (
              <Alert severity="warning">
                {tt(
                  "dialog.RemoveMobileKeyDialog.noMoreDevicesAfterRemovingAlert",
                )}
              </Alert>
            )}
            <Typography gutterBottom={false}>
              {tt("dialog.OnBehalfOf.textSentEmail", {
                email: emailShaded ?? "",
                name: authPersonName ?? "",
              })}
            </Typography>
            <OutlinedInput
              name="authorizationcode"
              value={authCode}
              onChange={onFieldChange}
              type="text"
              fullWidth
              // autofocus the field, so it's easier to enter the code
              autoFocus
              // Apple autocomplete from connected iPhone
              autoComplete="one-time-code"
            />
            <UserFriendlyId userFriendlyId={userFriendlyId} />
          </Box>
        </AppDialogContent>

        <AppDialogActions>
          <Box display={"flex"} justifyContent={"space-between"} width={"100%"}>
            <Button onClick={onClickResend} loading={isResendingCode}>
              {tt("dialog.btnResend")}
            </Button>
            <Button color="error" type="submit" loading={isSubmitting}>
              {tt("dialog.RemoveMobileKeyDialog.confirm")}
            </Button>
          </Box>
        </AppDialogActions>
      </form>
      {errorMessage ? (
        <Box sx={{ mx: 4, mb: 4 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      ) : null}
    </>
  );
}
